import { Box, Checkbox, Container, FormControlLabel, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import { MaterialReactTable, MRT_TableInstance } from "material-react-table";
import { useContext, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { StringParam, useQueryParam } from "use-query-params";
import Iconify from "../../../minimals/components/iconify";
import NotesModal from "../../checks/checkNotes/NotesModal";
import { UserContext } from "../../contexts/UserContext";
import { exportTableToCsv } from "../../taskUtils";
import { InternalUser } from "../../types/app";
import { NotesColumn } from "../NotesColumn";
import Actions from "./Actions";
import { columns } from "./columns";
import { NonZeroBalanceLedgersContext } from "./NonZeroBalanceLedgersContext";
import { saveNonZeroBalanceLedgerNote } from "./saveNote";
import { NonZeroBalanceLedger } from "./types";
import { DashboardReportType } from "../../types/reports";
import IncomingWiresModal from "./IncomingWiresModal";
import { MRT_ColumnDef } from "material-react-table";
import { fCurrency } from "../../utils/formatNumber";
import { Link } from "@mui/material";
import ReportBubbles from "../../orders/ReportBubbles";


const today = dayjs();
const defaultEndDate = null;
const defaultStartDate = null;

interface NonZeroBalanceLedgersProps {
  pageTitle?: string;
  reportSource?: "nonZeroBalances" | "deposits";
}

export default function NonZeroBalanceLedgers({
  pageTitle = "Non Zero Balance Ledgers",
  reportSource = "nonZeroBalances"
}: NonZeroBalanceLedgersProps) {
  const {
    nonZeroBalanceLedgers,
    loading,
    fetchNonZeroBalanceLedgers,
    assignedToMe,
    setAssignedToMe,
    setNonZeroBalanceLedgers,
    tableRef
  } = useContext(NonZeroBalanceLedgersContext);
  const { user } = useContext(UserContext);
  const [assignedToId] = useQueryParam("assignedtoid", StringParam);
  const [assignedToUser, setAssignedToUser] = useState<InternalUser | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<NonZeroBalanceLedger | null>(null);
  const [enableOrdering, setEnableOrdering] = useState(false);
  const [incomingWiresOrderNumber, setIncomingWiresOrderNumber] = useState<string | null>(null);
  const [dormantOnly, setDormantOnly] = useState(true);
  const [assignableInternalUserIds, setAssignableInternalUserIds] = useState<number[]>([]);

  const isDepositsReport = reportSource === "deposits";
  const isUnassignedOnly = Number(assignedToId) == -1;

  const getAndSetAssignedToUser = async () => {
    if (!assignedToId || isUnassignedOnly) {
      return;
    }
    const { data } = await axios.get<InternalUser>(
      `/api/clientphonebook/internalusers/getInternalUserById?id=${assignedToId}`
    );
    setAssignedToUser(data);
  };

  const getAndSetAssignableInternalUserIds = async () => {
    const id = assignedToId && !isUnassignedOnly ? Number(assignedToId) : user?.id;
    if (!id) {
      setAssignableInternalUserIds([]);
      return;
    }

    const { data } = await axios.get<number[]>(
      `/api/clientphonebook/internalusers/getInternalUserIdsIncludingPairedUsers?internalUserIds=${id}`
    );
    setAssignableInternalUserIds(data);
  };

  const openIncomingWires = (orderNumber: string) => {
    setIncomingWiresOrderNumber(orderNumber);
  };

  
  const toggleColumnOrdering = () => {
    setEnableOrdering(!enableOrdering);
  };

  const filteredNonZeroBalanceLedgers = useMemo(() => {
    let ledgers = nonZeroBalanceLedgers;
    if (isUnassignedOnly) {
      ledgers = ledgers.filter((o) => !o.assignedTo);
    } else if (assignedToId) {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : [Number(assignedToId)];
      ledgers = ledgers.filter((o) => !!o.assignedToUser?.id && assignedToIds.includes(o.assignedToUser.id));
    } else if (assignedToMe) {
      const assignedToIds = assignableInternalUserIds.length ? assignableInternalUserIds : user?.id ? [user.id] : [];
      ledgers = ledgers.filter((o) => !!o.assignedToUser?.id && assignedToIds.includes(o.assignedToUser.id));
    }

    if (isDepositsReport && dormantOnly) {
      ledgers = ledgers.filter((o) => !!o.isDormant);
    }

    return ledgers;
  }, [
    nonZeroBalanceLedgers,
    isUnassignedOnly,
    assignedToId,
    assignedToUser,
    assignedToMe,
    user?.email,
    user?.id,
    isDepositsReport,
    dormantOnly,
    assignableInternalUserIds
  ]);

  useEffect(() => {
    getAndSetAssignedToUser();
    getAndSetAssignableInternalUserIds();
  }, [assignedToId, user?.id, isDepositsReport]);

  const onRowsDeselected = () => {
    if (tableRef.current) {
      tableRef.current.toggleAllRowsSelected(false);
    }
  };
  const getTotal = (table: MRT_TableInstance<NonZeroBalanceLedger>) => {
    return loading ? 0 : table.getExpandedRowModel().rows.reduce((sum, row) => sum + (row.original?.amount || 0), 0);
  };

  const columnsWithNotes = NotesColumn<NonZeroBalanceLedger>(
      columns.map((col) => {
        if (col.accessorKey === "amount") {
          return {
            ...col,
            Cell: ({ row }) =>
                row.original.amount ? (
                    <Link
                        component="span"
                        role="button"
                        tabIndex={0}
                        underline="none"
                        onClick={() => openIncomingWires(row.original.name)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            openIncomingWires(row.original.name);
                          }
                        }}
                        sx={{ cursor: "pointer", userSelect: "text" }}
                    >
                      {fCurrency(row.original.amount)}
                    </Link>
                ) : (
                    <></>
                )
          };
        }
        return col;
      }) as MRT_ColumnDef<NonZeroBalanceLedger>[],
      setSelectedOrder
  );


  return (
    <>
      <Helmet>
        <title>{pageTitle} | TitleQ</title>
      </Helmet>

      <Container maxWidth="xl">
        <Box sx={{ mb: 2 }}>
          <ReportBubbles excludedPermissions={["PolicyIssues", "PoliciesToSend"]} />
        </Box>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h4" marginBottom={2}>
            {pageTitle}
            {isUnassignedOnly && " - Unassigned"}
            {assignedToUser && ` - Assigned to ${assignedToUser.firstName} ${assignedToUser.lastName}`}
          </Typography>
        </Stack>
        <MaterialReactTable
          enableStickyHeader
          columns={columnsWithNotes}
          data={filteredNonZeroBalanceLedgers}
          enableGlobalFilter
          enableFacetedValues
          globalFilterFn="includesString"
          enableColumnOrdering={enableOrdering}
          initialState={{
            density: "compact",
            pagination: { pageIndex: 0, pageSize: 30 },
            showColumnFilters: true,
            columnVisibility: {
              dueDate: false,
              for: false
            },
            columnFilters: [
              {
                id: "dueDate",
                value: [defaultStartDate, defaultEndDate]
              }
            ]
          }}
          state={{
            showSkeletons: loading
          }}
          muiTableBodyProps={{
            sx: {
              "& .MuiTableRow-root:hover .MuiTableCell-root": {
                backgroundColor: "inherit !important"
              }
            }
          }}
          muiTableBodyRowProps={({ row }) => {
            // Apply zebra striping styles to alternate rows
            return {
              style: row.index % 2 === 0 ? { backgroundColor: "#f4f4f4" } : {} // Change the background color as needed
            };
          }}
          renderTopToolbarCustomActions={({ table }) => {
            return (
              <>
                <Actions
                  assignedToMe={assignedToMe}
                  setAssignedToMe={setAssignedToMe}
                  count={loading ? null : table.getRowCount()}
                  total={getTotal(table)}
                  hideTotal={false}
                  hideAssignedToMeOptions={!!assignedToId}
                  hideReasignAction={true}
                  table={table}
                  fetchTableData={() => fetchNonZeroBalanceLedgers(true)}
                  reportType={DashboardReportType.EscrowOverdue}
                  onRowsDeselected={onRowsDeselected}
                />
                {isDepositsReport && (
                  <FormControlLabel
                    control={<Checkbox checked={dormantOnly} onChange={(_, value) => setDormantOnly(value)} />}
                    label="Dormant Only"
                  />
                )}
                <Box sx={{ flexGrow: 1, position: "relative" }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ position: "absolute", right: 0 }}>
                      <Tooltip title="Column ordering">
                        <IconButton onClick={toggleColumnOrdering}>
                          <Iconify icon="mdi:reorder-vertical" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Export to csv">
                        <IconButton onClick={() => exportTableToCsv(table, "export.csv")}>
                          <Iconify icon="eva:download-fill" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Box>
              </>
            );
          }}
        />
      </Container>
      {!!selectedOrder && (
        <NotesModal
          isOpen
          nameBeforeUpdate={selectedOrder?.assignedTo || ""}
          emailBeforeUpdate={
            selectedOrder?.assignedToEmail && selectedOrder.assignedToEmail.trim().toLowerCase() !== "null"
              ? selectedOrder.assignedToEmail.trim()
              : selectedOrder?.assignedToUser?.email || ""
          }
          entity={selectedOrder}
          setEntity={setSelectedOrder}
          setEntityTable={setNonZeroBalanceLedgers}
          reportType={DashboardReportType.NonZeroLedgerBalances}
          saveLedgerNote={async (text) => {
            await saveNonZeroBalanceLedgerNote(selectedOrder.name, selectedOrder?.amount, text, user?.name as string);
          }}
        />
      )}

      {incomingWiresOrderNumber && (
          <IncomingWiresModal
              open={!!incomingWiresOrderNumber}
              orderNumber={incomingWiresOrderNumber}
              onClose={() => setIncomingWiresOrderNumber(null)}
          />
      )}

    </>
  );
}
